/** How wide a day's label is given room on the axis, in px: "30 Sept" and a gap either side. */
const labelRoom = 60

/** Which of a range's days stand on one calendar step. */
type Step = (days: readonly string[]) => string[]

/** Every `n`th day, counted back from the last, so the last is always one. */
const everyNth =
  (n: number): Step =>
  (days) =>
    days.filter((_, index) => (days.length - 1 - index) % n === 0)

const isMonday = (day: string) => new Date(`${day}T00:00:00Z`).getUTCDay() === 1

const nthMondays =
  (n: number): Step =>
  (days) =>
    days.filter(isMonday).filter((_, index) => index % n === 0)

const monthFirsts =
  (n: number): Step =>
  (days) =>
    days.filter((day) => day.endsWith("-01") && (Number(day.slice(5, 7)) - 1) % n === 0)

const steps: readonly Step[] = [
  everyNth(1),
  everyNth(2),
  nthMondays(1),
  nthMondays(2),
  monthFirsts(1),
  monthFirsts(3),
  monthFirsts(12),
]

/**
 * The days a day axis labels, at one even calendar step: the finest of every day, every other day,
 * each Monday, every other Monday, each month's first, each quarter's or each year's whose labels
 * all have room in `width` px, so the gaps between labels never vary along the axis. The range's
 * last day, where its newest bars stand (today, on a preset range), is always labelled: the days
 * steps are counted back from it, and a calendar step gives it a label of its own, the step's last
 * giving way where the two would stand too close.
 */
export function dayTicksOf(days: readonly string[], width: number) {
  const last = days.at(-1)
  if (last === undefined) return []
  const room = Math.max(2, Math.floor(width / labelRoom))
  const dayWidth = width / days.length
  const withLast = (ticks: string[]) => {
    if (ticks.at(-1) === last) return ticks
    const apart = (day: string) => (days.length - 1 - days.indexOf(day)) * dayWidth >= labelRoom
    return [...ticks.filter(apart), last]
  }
  for (const step of steps) {
    const ticks = withLast(step(days))
    if (ticks.length <= room) return ticks
  }
  return [last]
}
